// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SectionWrapper, { SectionWrapperStyles } from "@pacific/App/Components/Common/SectionWrapper";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { getCustomTypeSession } from "@pacific/App/Store/Selectors/SessionSelectors";
import InfoCard from "@pacific/App/Widgets/InfoCards/InfoCard";
import { xScreen } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface InfoCardFields {
    fields: {
        title: string;
        buttonText: string;
        description: string;
        buttonUrl: LinkFieldValue;
        imageUrl: string;
        icon: string;
        openInNewTab: boolean;
        iconSize: number;
        linkElementId: string;
        gaEvent: string;
    };
}

const enum fields {
    title = "title",
    cards = "cards",
    subtitle = "subtitle",
    subtitleUrl = "subtitleUrl",
    openInNewTab = "openInNewTab",
    subTitleEvent = "subTitleEvent",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.subtitle]: string;
        [fields.subtitleUrl]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
        [fields.cards]: InfoCardFields[];
        [fields.subTitleEvent]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: xIsAuthenticated(state),
    session: getCustomTypeSession(state),
    currentBillTo: getCurrentBillToState(state).value,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

interface InfoCardsWidgetStyles {
    container?: InjectableCss;
    content?: GridItemProps;
    sectionWrapperStyles?: SectionWrapperStyles;
}

export const infoCardsWidgetStyles: InfoCardsWidgetStyles = {
    container: {
        css: css`
            padding-top: 0;
            gap: 22px;
            height: 100%;
            display: flex;
            flex-wrap: wrap;
            ${xScreen("lte", "md")} {
                gap: 20px 14px;
            }

            ${xScreen("lte", "sm")} {
                gap: 20px;
            }
        `,
    },
    content: {
        css: css`
            ${({ numberOfCards }: any) => {
                if (numberOfCards >= 3) {
                    return css`
                        width: calc((100% - 44px) / 3);
                        ${xScreen("lte", "md")} {
                            width: calc(50% - 7px);
                        }
                    `;
                    // eslint-disable-next-line no-else-return
                } else if (numberOfCards === 2) {
                    return css`
                        width: calc((100% - 22px) / 2);
                        ${xScreen("lte", "md")} {
                            width: calc(50% - 7px);
                        }
                    `;
                } else {
                    return css`
                        width: 100%;
                    `;
                }
            }}
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    sectionWrapperStyles: {
        wrapper: {
            css: css`
                & > div {
                    height: 100%;
                }
            `,
        },
    },
};

const InfoCardsWidget: React.FC<Props> = ({
    fields: { title, subtitle, subtitleUrl, openInNewTab = false, cards, subTitleEvent },
    currentBillTo,
    isAuthenticated,
    session,
}) => {
    if (!cards || cards.length === 0) {
        return null;
    }
    const styles = infoCardsWidgetStyles;
    const styledProps = {
        numberOfCards: cards.length,
    };
    
    const _GaInfoCardEvents = (event?: string, title?: string, url?: string) => {
        const dataLayer = getDataLayer();
        if (!dataLayer || !event) {
            return;
        }
        const googleAnalyticsData = {
            event,
            click_text: title,
            destination: url,
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            affiliation: currentBillTo?.companyName || "",
            sales_rep: (session as any)?.salesperson?.name || "",
        };
        dataLayer.push(googleAnalyticsData);
    };

    return (
        <SectionWrapper
            title={title}
            openInNewTab={openInNewTab}
            linkText={subtitle}
            link={subtitleUrl}
            onLinkClick={() => _GaInfoCardEvents(subTitleEvent, subtitle, subtitleUrl.value)}
            extendedStyles={styles.sectionWrapperStyles}
            data-test-selector="Pacific-InfoCardsWidget"
        >
            <StyledWrapper {...styles.container} className="infoCardsWrapper">
                {cards.map((card, index) => (
                    <React.Fragment key={index.toString()}>
                        <StyledWrapper {...styles.content} {...styledProps}>
                            <InfoCard
                                buttonText={card.fields.buttonText}
                                title={card.fields.title}
                                description={
                                    card.fields.description.length > 200
                                        ? card.fields.description.substring(0, 200).concat("...")
                                        : card.fields.description
                                }
                                buttonUrl={card.fields.buttonUrl}
                                imageUrl={card.fields.imageUrl}
                                icon={card.fields.icon}
                                openInNewTab={card.fields.openInNewTab}
                                iconSize={card.fields.iconSize}
                                linkElementId={card.fields.linkElementId}
                                gaEvent={card.fields.gaEvent}
                            />
                        </StyledWrapper>
                    </React.Fragment>
                ))}
            </StyledWrapper>
        </SectionWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(InfoCardsWidget),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.subtitle,
                displayName: "Link Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.subtitleUrl,
                displayName: "Link Url",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
                isVisible: item => item.fields.subtitle,
            },
            {
                name: fields.openInNewTab,
                displayName: "Open in New Tab",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                isVisible: item => item.fields.subtitle,
            },
            {
                name: fields.subTitleEvent,
                displayName: "GA Event Name",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isVisible: item => item.fields.subtitle,
            },
            {
                name: fields.cards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "imageUrl",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                        maxLength: 40,
                    },
                    {
                        name: "description",
                        displayName: "Description",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                    },
                    {
                        name: "buttonText",
                        displayName: "Button Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "buttonUrl",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: true,
                    },
                    {
                        name: "openInNewTab",
                        displayName: "Open in New Tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "icon",
                        displayName: "Button Icon",
                        editorTemplate: "DropDownField",
                        options: [
                            {
                                displayName: "PlayCircle",
                                value: "PlayCircle",
                            },
                            {
                                displayName: "ShoppingBag",
                                value: "ShoppingBag",
                            },
                            {
                                displayName: "CaretRight",
                                value: "CaretRight",
                            },
                        ],
                        defaultValue: "CaretRight",
                    },
                    {
                        name: "iconSize",
                        displayName: "Icon Size",
                        editorTemplate: "IntegerField",
                        defaultValue: 14,
                        min: 5,
                        max: 30,
                    },
                    {
                        name: "linkElementId",
                        displayName: "Link Element Id",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "gaEvent",
                        displayName: "GA Event Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
